import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Page from "../Page";
import UnlockIcon from "../../assets/unlock.svg";
import LightningIcon from "../../assets/lightning.svg";
import HeartIcon from "../../assets/heart.svg";
import StripeImage from "../../assets/stripe.png";
import SecureIcon from "../../assets/secure.svg";
import { Button, CircularProgress, Typography } from "@mui/material";
import useSubscriptionAPIClient from "../../api/subscription/hooks/useSubscriptionAPIClient";
import useRevenueCat from "../../hooks/subscription/useRevenueCat";
import { Capacitor } from "@capacitor/core";
import { dispatch } from "../../redux/store";
import { setUserSubscription } from "../../redux/actions/user";

const SubscribePage = () => {
  const { getCheckoutLink, getSubscriptionStatus } = useSubscriptionAPIClient();
  const { purchaseMonthlySubscription } = useRevenueCat();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const hasTriggeredPurchase = useRef(false);

  // On mobile, bypass the Subscribe page and go straight to Play Store purchase
  useEffect(() => {
    if (Capacitor.isNativePlatform() && !hasTriggeredPurchase.current) {
      hasTriggeredPurchase.current = true;
      (async () => {
        setLoading(true);
        try {
          const result = await purchaseMonthlySubscription();
          if (result?.success) {
            // Optimistically update subscription state immediately
            dispatch(
              setUserSubscription({ plan: "member", status: "active" }),
            );
            // Then verify with backend (webhook may not have arrived yet)
            try {
              const subscription = await getSubscriptionStatus();
              dispatch(setUserSubscription(subscription));
            } catch (error) {
              // Keep optimistic state — backend will sync on next app load
              console.error("Failed to fetch subscription status after purchase:", error);
            }
            navigate("/dashboard");
          } else {
            // User cancelled or purchase failed — go back
            navigate(-1);
          }
        } catch (error) {
          console.error("Purchase flow error:", error);
          navigate(-1);
        } finally {
          setLoading(false);
        }
      })();
    }
  }, []);

  const onSubscribeClick = async () => {
    // Clear any stale checkout flag (user may have cancelled a previous attempt)
    localStorage.removeItem("pendingStripeCheckout");
    setLoading(true);
    try {
      const checkoutUrl = await getCheckoutLink();
      // Flag that we're heading to Stripe checkout so the app can
      // optimistically update subscription state on return
      localStorage.setItem("pendingStripeCheckout", "true");
      window.location.href = checkoutUrl;
    } finally {
      setLoading(false);
    }
  };

  // On mobile, show a loading state while the Play Store purchase flow runs
  if (Capacitor.isNativePlatform()) {
    return (
      <Page className="subscribe-page" pageKey="subscribe">
        <div className="subscribe-page_container" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
          <CircularProgress />
        </div>
      </Page>
    );
  }

  return (
    <Page className="subscribe-page" pageKey="subscribe">
      <div className="subscribe-page_container">
        <div className="subscribe-page_panel">
          <Typography
            variant="pageTitleDark"
            className="subscribe-page_panel-title"
          >
            Subscribe to Mindstack
          </Typography>
          <Typography variant="p1">
            By subscribing to Mindstack you will become a supporting member and
            receive a number of benefits:
          </Typography>
          <div className="subscribe-page_benefits-container">
            <div className="subscribe-page_benefit">
              <img
                src={LightningIcon}
                className="subscribe-page_benefit-icon"
              />
              <Typography
                variant="h2NoMargin"
                className="subscribe-page_benefit-title"
              >
                Unlimited Tasks
              </Typography>
              <Typography
                variant="body1"
                className="subscribe-page_benefit-description"
              >
                Create and manage as many tasks as you need.
              </Typography>
            </div>
            <div className="subscribe-page_benefit">
              <img src={UnlockIcon} className="subscribe-page_benefit-icon" />
              <Typography
                variant="h2NoMargin"
                className="subscribe-page_benefit-title"
              >
                Exclusive Features
              </Typography>
              <Typography
                variant="body1"
                className="subscribe-page_benefit-description"
              >
                Gain access to paid features with no restricted content.
              </Typography>
            </div>
            <div className="subscribe-page_benefit">
              <img src={HeartIcon} className="subscribe-page_benefit-icon" />
              <Typography
                variant="h2NoMargin"
                className="subscribe-page_benefit-title"
              >
                Support Our Journey
              </Typography>
              <Typography
                variant="body1"
                className="subscribe-page_benefit-description"
              >
                Your subscription directly supports the growth of Mindstack and
                helps us build something great.
              </Typography>
            </div>
          </div>

          <div className="subscribe-page_price-container">
            <Typography
              variant="h2NoMargin"
              className="subscribe-page_price-label"
            >
              For only £3.69/month
            </Typography>
          </div>

          <div className="subscribe-page_subscribe-button-container">
            <Button
              variant="contained"
              className="subscribe-page_subscribe-button"
              onClick={onSubscribeClick}
              loading={loading}
            >
              {loading ? (
                <CircularProgress color="white" />
              ) : (
                <span
                  style={{
                    color: "#FFFFFF",
                    textTransform: "none",
                  }}
                >
                  Subscribe to Mindstack
                </span>
              )}
            </Button>
          </div>
          {/* Stripe disclaimer — page only renders on web */}
          <div className="subscribe-page_stripe-disclaimer-container">
            <div className="subscribe-page_stripe-disclaimer">
              <img
                src={SecureIcon}
                className="subscribe-page_stripe-disclaimer-icon"
              />
              <div className="subscribe-page_stripe-disclaimer-text">
                <Typography variant="body1">
                  Payments are processed securely by Stripe.
                </Typography>
                <Typography variant="body1">
                  You will be redirected to their secure checkout to complete
                  your purchase.
                </Typography>
              </div>

              <img
                src={StripeImage}
                className="subscribe-page_stripe-image"
              />
            </div>
          </div>
        </div>
      </div>
    </Page>
  );
};

export default SubscribePage;
